/**
 * SellOrdersTracker — tus sell orders activas agrupadas por hub.
 *
 *  - Summary tiles (total ISK locked, units, expiring soon count)
 *  - Group cards por location (con ISK + volume totals)
 *  - Filtro por status (active / expiring_soon / all)
 *  - Search box por type name
 *  - Tabla con qty remaining, price, days until expiry
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from './LastUpdated'
import { RefreshButton } from './RefreshButton'
import { LoadingSkeleton } from './LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'

type Status = 'active' | 'expiring_soon' | 'expired'

type Order = {
  order_id: number
  type_id: number
  type_name: string
  location_id: number
  location_name: string
  price: number
  volume_total: number
  volume_remain: number
  total_isk: number
  issued: string
  expires: string
  duration_days: number
  days_until_expiry: number
  status: Status
  range: string
  min_volume: number
}

const STATUS_FILTERS: Array<{ key: Status | 'all'; label: string }> = [
  { key: 'all', label: 'Todos' },
  { key: 'active', label: 'Activas' },
  { key: 'expiring_soon', label: 'Expiran pronto' },
  { key: 'expired', label: 'Expiradas' },
]

function statusClasses(s: Status): string {
  switch (s) {
    case 'active':
      return 'bg-eve-success/10 border-eve-success text-eve-success'
    case 'expiring_soon':
      return 'bg-eve-warn/10 border-eve-warn text-eve-warn'
    case 'expired':
      return 'bg-eve-muted/10 border-eve-border text-eve-muted'
  }
}

function statusLabel(s: Status): string {
  switch (s) {
    case 'active':
      return 'Activa'
    case 'expiring_soon':
      return 'Expira pronto'
    case 'expired':
      return 'Expirada'
  }
}

export function SellOrdersTracker() {
  const [filter, setFilter] = useState<Status | 'all'>('all')
  const [search, setSearch] = useState('')
  const { key: forceRefetchKey, force, bump } = useForceRefetch()

  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['utility-active-sell-orders', forceRefetchKey],
    queryFn: () => api.activeSellOrders(force),
  })

  const items: Order[] = data?.items ?? []
  const summary = data?.summary
  const groups = data?.groups ?? []

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter((o) => {
      if (filter !== 'all' && o.status !== filter) return false
      if (q && !o.type_name.toLowerCase().includes(q) && !o.location_name.toLowerCase().includes(q)) {
        return false
      }
      return true
    })
  }, [items, filter, search])

  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="stat-label">Sell orders tracking</div>
          <div className="text-eve-muted text-xs">
            Tus órdenes activas en todos los hubs, agrupadas por ubicación.
            Cuánto ISK tenés lockeado, cuándo expiran.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* Summary tiles */}
      {summary && summary.total_orders > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <SummaryTile label="Órdenes activas" value={String(summary.total_orders)} sub={`en ${summary.total_locations} ubicación${summary.total_locations === 1 ? '' : 'es'}`} />
          <SummaryTile
            label="ISK lockeado"
            value={`${formatISKShort(summary.total_isk_locked)} ISK`}
            sub={`${summary.total_units_remaining.toLocaleString('en-US')} unidades restantes`}
            positive
          />
          <SummaryTile
            label="Expira pronto"
            value={String(summary.expiring_soon)}
            sub="≤ 7 días"
            warning={summary.expiring_soon > 0}
          />
          <SummaryTile
            label="Promedio por orden"
            value={
              summary.total_orders > 0
                ? `${formatISKShort(summary.total_isk_locked / summary.total_orders)} ISK`
                : '—'
            }
            sub={`${Math.round(summary.total_units_remaining / Math.max(1, summary.total_orders))} unidades c/u`}
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar item o hub…"
          className="bg-eve-panel border border-eve-border rounded-md px-3 py-1.5 text-sm text-eve-text placeholder:text-eve-muted focus:border-eve-accent focus:outline-none w-full md:w-64"
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as Status | 'all')}
          className="bg-eve-panel border border-eve-border rounded-md px-2 py-1.5 text-sm text-eve-text focus:border-eve-accent focus:outline-none"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
        <span className="text-eve-muted text-xs ml-auto">
          {filtered.length} / {items.length}
        </span>
      </div>

      {/* Groups */}
      {isLoading ? (
        <LoadingSkeleton variant="rows" rows={6} />
      ) : summary?.total_orders === 0 ? (
        <div className="text-eve-muted py-8 text-center italic">
          No tenés sell orders activas. Listá algo en el market y van a aparecer acá.
        </div>
      ) : groups.length === 0 ? (
        <div className="text-eve-muted py-8 text-center italic">Sin matches.</div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => {
            // Filter orders within the group by the current filter/search
            const visibleOrders = g.orders.filter((o) => {
              if (filter !== 'all' && o.status !== filter) return false
              if (search) {
                const q = search.trim().toLowerCase()
                if (!o.type_name.toLowerCase().includes(q) && !o.location_name.toLowerCase().includes(q)) {
                  return false
                }
              }
              return true
            })
            if (visibleOrders.length === 0) return null
            return <LocationGroup key={g.location_id} group={g} visibleOrders={visibleOrders} />
          })}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

function SummaryTile({
  label,
  value,
  sub,
  positive,
  warning,
}: {
  label: string
  value: string
  sub?: string
  positive?: boolean
  warning?: boolean
}) {
  const valueClass = positive
    ? 'text-eve-success'
    : warning
      ? 'text-eve-warn'
      : 'text-eve-text'
  return (
    <div className="bg-eve-bg border border-eve-border rounded-md p-3">
      <div className="stat-label">{label}</div>
      <div className={`font-mono text-lg font-semibold mt-1 ${valueClass}`}>{value}</div>
      {sub && <div className="text-[10px] text-eve-muted mt-1 italic">{sub}</div>}
    </div>
  )
}

function LocationGroup({
  group,
  visibleOrders,
}: {
  group: {
    location_id: number
    location_name: string
    total_isk: number
    total_volume: number
  }
  visibleOrders: Order[]
}) {
  return (
    <details open className="border border-eve-border rounded-md bg-eve-bg/40">
      <summary className="cursor-pointer p-3 flex items-center gap-3 hover:bg-eve-panel/50 transition-colors">
        <div className="min-w-0 flex-1">
          <div className="font-medium">{group.location_name}</div>
          <div className="text-xs text-eve-muted font-mono">
            {visibleOrders.length} orden{visibleOrders.length === 1 ? '' : 'es'} · {group.total_volume.toLocaleString('en-US')} unidades
          </div>
        </div>
        <div className="text-right">
          <div className="stat-label">ISK lockeado</div>
          <div className="font-mono text-base font-semibold text-eve-accent">
            {formatISKShort(group.total_isk)} ISK
          </div>
        </div>
      </summary>
      <div className="border-t border-eve-border">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-eve-muted uppercase tracking-wider bg-eve-panel/30">
              <th className="py-2 px-3 font-medium">Item</th>
              <th className="py-2 px-3 font-medium text-right">Qty remain</th>
              <th className="py-2 px-3 font-medium text-right">Price</th>
              <th className="py-2 px-3 font-medium text-right">Total ISK</th>
              <th className="py-2 px-3 font-medium text-right">Expira</th>
              <th className="py-2 px-3 font-medium text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {visibleOrders.map((o) => (
              <tr key={o.order_id} className="border-b border-eve-border/40 hover:bg-eve-panel/30">
                <td className="py-2 px-3">
                  <div className="font-medium">{o.type_name}</div>
                  <div className="text-[10px] text-eve-muted font-mono">
                    Order #{o.order_id} · range {o.range}
                  </div>
                </td>
                <td className="py-2 px-3 text-right font-mono">
                  {o.volume_remain.toLocaleString('en-US')}
                  <span className="text-eve-muted text-[10px]">/{o.volume_total.toLocaleString('en-US')}</span>
                </td>
                <td className="py-2 px-3 text-right font-mono">
                  {formatISK(o.price, 2)}
                </td>
                <td className="py-2 px-3 text-right font-mono text-eve-accent">
                  {formatISKShort(o.total_isk)}
                </td>
                <td className="py-2 px-3 text-right font-mono">
                  {o.days_until_expiry}d
                </td>
                <td className="py-2 px-3 text-center">
                  <span
                    className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border whitespace-nowrap ${statusClasses(o.status)}`}
                  >
                    {statusLabel(o.status)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}
